import { create as createEngine } from "./engine.js";
import { TableEngineIO } from "./plugins/io.js";

function defaultPluginError(phase, plugin, err) {
  console.error(`[TableEngine plugin lifecycle failed (${phase})]`, plugin?.name || plugin, err);
}

function isEngineAlive(eng) {
  if (!eng || typeof eng !== "object") return false;
  if (eng.__tePluginDestroyed === true) return false;
  return true;
}

function resolvePluginDisposer(ret) {
  if (!ret) return null;
  if (typeof ret === "function") return ret;
  if (typeof ret.dispose === "function") return () => ret.dispose();
  if (typeof ret.destroy === "function") return () => ret.destroy();
  return null;
}

function createEventBus() {
  if (typeof EventTarget === "function") return new EventTarget();

  const listeners = new Map();
  return {
    addEventListener(type, handler) {
      if (!type || typeof handler !== "function") return;
      const key = String(type);
      const bucket = listeners.get(key) || new Set();
      bucket.add(handler);
      listeners.set(key, bucket);
    },
    removeEventListener(type, handler) {
      if (!type || typeof handler !== "function") return;
      const key = String(type);
      const bucket = listeners.get(key);
      if (!bucket) return;
      bucket.delete(handler);
      if (!bucket.size) listeners.delete(key);
    },
    dispatchEvent(event) {
      const type = String(event?.type || "");
      if (!type) return true;
      const bucket = listeners.get(type);
      if (!bucket || !bucket.size) return true;

      for (const handler of [...bucket]) {
        try {
          handler.call(this, event);
        } catch (err) {
          setTimeout(() => {
            throw err;
          }, 0);
        }
      }
      return true;
    },
  };
}

function createDetailEvent(type, detail) {
  const name = String(type || "").trim();
  if (!name) return null;

  if (typeof CustomEvent === "function") {
    return new CustomEvent(name, { detail });
  }

  if (typeof Event === "function") {
    const evt = new Event(name);
    try {
      evt.detail = detail;
    } catch {}
    return evt;
  }

  return { type: name, detail };
}

function pluginNameOf(plugin) {
  return String(plugin?.name || plugin || "<anonymous>").trim() || "<anonymous>";
}

function normalizedRef(value) {
  const key = String(value || "").trim();
  return key || null;
}

function parseInstanceFilter(ref) {
  if (!ref) return null;
  if (typeof ref === "string") {
    const id = normalizedRef(ref);
    return id ? { id } : null;
  }
  if (typeof ref !== "object") return null;

  const id = normalizedRef(ref.id || ref.instanceId);
  const tableId = normalizedRef(ref.tableId);
  const rootId = normalizedRef(ref.rootId);
  const specId = normalizedRef(ref.specId || ref.specName);

  if (!id && !tableId && !rootId && !specId) return null;
  return { id, tableId, rootId, specId };
}

export const TABLE_ENGINE_WEB_API_VERSION = "2.1.0";

export const TABLE_ENGINE_WEB_EVENTS = Object.freeze({
  INSTANCE_CREATED: "instance:created",
  INSTANCE_DESTROYED: "instance:destroyed",
  INSTANCE_CREATE_FAILED: "instance:create-failed",
  INSTANCE_DESTROY_FAILED: "instance:destroy-failed",
  PLUGIN_REGISTERED: "plugin:registered",
  PLUGIN_REGISTER_SKIPPED: "plugin:register-skipped",
  PLUGIN_UNREGISTERED: "plugin:unregistered",
  PLUGIN_UNREGISTER_SKIPPED: "plugin:unregister-skipped",
  SPEC_REGISTERED: "spec:registered",
  SPEC_REGISTER_SKIPPED: "spec:register-skipped",
});

export function createStandaloneTableEngine(options = {}) {
  const {
    specRegistry = {},
    installWindowExports = false,
    onPluginInitError = null,
  } = options || {};

  const registry = specRegistry && typeof specRegistry === "object" ? specRegistry : {};
  const plugins = [];
  const engines = [];
  const instancesById = new Map();
  const eventBus = createEventBus();
  let nextInstanceId = 1;

  const reportPluginError =
    typeof onPluginInitError === "function" ? onPluginInitError : defaultPluginError;

  function emit(eventType, detail = {}) {
    const evt = createDetailEvent(eventType, detail);
    if (!evt) return;
    try {
      eventBus.dispatchEvent(evt);
    } catch {}
  }

  function uniqueInstanceId(base) {
    let id = String(base || "").trim();
    if (!id) id = `te-${nextInstanceId++}`;

    if (!instancesById.has(id)) return id;

    const root = id;
    let suffix = 2;
    while (instancesById.has(id)) {
      id = `${root}-${suffix}`;
      suffix += 1;
    }
    return id;
  }

  function nextInstanceLabel(spec) {
    const preferred =
      String(spec?.id || spec?.tableId || spec?.rootId || "").trim() || `te-${nextInstanceId++}`;
    return uniqueInstanceId(preferred);
  }

  function summarizeEntry(entry) {
    if (!entry) return null;

    const spec = entry.ctx?.spec || {};
    const eng = entry.eng || null;

    return {
      id: entry.instanceId || null,
      tableId: spec?.tableId || eng?.table?.id || null,
      rootId: spec?.rootId || eng?.root?.id || null,
      specId: spec?.id || null,
      createdAt: entry.createdAt || null,
    };
  }

  function entryMatchesFilter(entry, filter) {
    if (!entry || !filter) return false;
    const summary = summarizeEntry(entry);
    if (!summary) return false;

    if (filter.id && summary.id !== filter.id) return false;
    if (filter.tableId && summary.tableId !== filter.tableId) return false;
    if (filter.rootId && summary.rootId !== filter.rootId) return false;
    if (filter.specId && summary.specId !== filter.specId) return false;

    return true;
  }

  function detachEntry(entry, reason = "destroy") {
    if (!entry || entry._detached) return;
    entry._detached = true;

    if (entry.instanceId && instancesById.get(entry.instanceId) === entry) {
      instancesById.delete(entry.instanceId);
    }

    emit(TABLE_ENGINE_WEB_EVENTS.INSTANCE_DESTROYED, {
      reason,
      instance: summarizeEntry(entry),
      engine: entry.eng || null,
    });
  }

  function resolveEntry(ref) {
    if (!ref) return null;

    if (typeof ref === "string") {
      return instancesById.get(String(ref).trim()) || null;
    }

    if (typeof ref === "object") {
      const filter = parseInstanceFilter(ref);
      if (filter?.id) {
        return instancesById.get(filter.id) || null;
      }
      if (filter) {
        for (const entry of engines) {
          if (!entry) continue;
          if (entryMatchesFilter(entry, filter)) return entry;
        }
      }
    }

    for (const entry of engines) {
      if (!entry) continue;
      if (entry === ref || entry.eng === ref) return entry;
    }

    return null;
  }

  function resolveEntries(ref) {
    const filter = parseInstanceFilter(ref);
    if (!filter) return [];
    return engines.filter((entry) => entryMatchesFilter(entry, filter));
  }

  function runPluginCleanup(plugin, entry, phase = "cleanup") {
    if (!plugin || !entry) return;

    const disposer = entry.pluginDisposers.get(plugin);
    entry.pluginDisposers.delete(plugin);

    if (typeof disposer === "function") {
      try {
        disposer();
      } catch (err) {
        reportPluginError(`${phase}:dispose`, plugin, err);
      }
    }

    if (typeof plugin.destroy === "function") {
      try {
        plugin.destroy(entry.eng, entry.ctx);
      } catch (err) {
        reportPluginError(`${phase}:destroy`, plugin, err);
      }
    }

    entry.initializedPlugins.delete(plugin);
  }

  function runEntryPluginCleanup(entry, phase = "cleanup") {
    if (!entry || entry.pluginCleanupDone === true) return;
    entry.pluginCleanupDone = true;

    const pluginSet = new Set([
      ...entry.initializedPlugins,
      ...entry.pluginDisposers.keys(),
    ]);

    for (const plugin of pluginSet) {
      runPluginCleanup(plugin, entry, phase);
    }

    entry.initializedPlugins.clear();
    entry.pluginDisposers.clear();
  }

  function pruneEngines() {
    for (let i = engines.length - 1; i >= 0; i--) {
      const entry = engines[i];
      if (isEngineAlive(entry?.eng)) continue;
      runEntryPluginCleanup(entry, "prune");
      detachEntry(entry, "prune");
      engines.splice(i, 1);
    }
  }

  function removeEngine(eng, reason = "destroy") {
    for (let i = engines.length - 1; i >= 0; i--) {
      const entry = engines[i];
      if (entry?.eng !== eng) continue;
      detachEntry(entry, reason);
      engines.splice(i, 1);
    }
  }

  function wrapDestroyForCleanup(entry) {
    const eng = entry?.eng;
    if (!eng || typeof eng !== "object") return;
    if (typeof eng.destroy !== "function") return;
    if (eng.__tePluginDestroyWrapped === true) return;

    const origDestroy = eng.destroy;
    try {
      eng.__tePluginDestroyWrapped = true;
      eng.destroy = function wrappedPluginDestroy(...args) {
        runEntryPluginCleanup(entry, "destroy");

        try {
          return origDestroy.apply(this, args);
        } finally {
          try {
            eng.__tePluginDestroyed = true;
          } catch {}
          removeEngine(eng, "destroy");
        }
      };
    } catch {
      // best effort
    }
  }

  function runPluginInit(plugin, entry, phase) {
    if (!plugin || !entry) return;
    if (entry.initializedPlugins.has(plugin)) return;

    try {
      const ret = plugin.init?.(entry.eng, entry.ctx);
      const disposer = resolvePluginDisposer(ret);
      if (typeof disposer === "function") {
        entry.pluginDisposers.set(plugin, disposer);
      }
      entry.initializedPlugins.add(plugin);
    } catch (err) {
      reportPluginError(phase, plugin, err);
    }
  }

  function registerPlugin(plugin) {
    if (!plugin) {
      emit(TABLE_ENGINE_WEB_EVENTS.PLUGIN_REGISTER_SKIPPED, {
        reason: "invalid-plugin",
      });
      return false;
    }
    if (plugins.includes(plugin)) {
      emit(TABLE_ENGINE_WEB_EVENTS.PLUGIN_REGISTER_SKIPPED, {
        reason: "duplicate",
        plugin: pluginNameOf(plugin),
      });
      return false;
    }
    plugins.push(plugin);
    pruneEngines();

    for (const entry of engines) {
      if (!isEngineAlive(entry?.eng)) continue;
      runPluginInit(plugin, entry, "late-init");
    }

    emit(TABLE_ENGINE_WEB_EVENTS.PLUGIN_REGISTERED, {
      plugin: pluginNameOf(plugin),
      totalPlugins: plugins.length,
    });

    return true;
  }

  function unregisterPlugin(pluginOrName) {
    const name = String(pluginOrName?.name || pluginOrName || "").trim();
    let removed = 0;
    const removedPlugins = [];

    for (let i = plugins.length - 1; i >= 0; i--) {
      const p = plugins[i];
      if (p === pluginOrName || (name && String(p?.name || "") === name)) {
        plugins.splice(i, 1);
        removedPlugins.push(p);
        removed += 1;
      }
    }

    if (removedPlugins.length) {
      pruneEngines();
      for (const entry of engines) {
        if (!isEngineAlive(entry?.eng)) continue;
        for (const plugin of removedPlugins) {
          runPluginCleanup(plugin, entry, "unregister");
        }
      }

      emit(TABLE_ENGINE_WEB_EVENTS.PLUGIN_UNREGISTERED, {
        removed,
        plugins: removedPlugins.map((plugin) => pluginNameOf(plugin)),
        totalPlugins: plugins.length,
      });
    } else {
      emit(TABLE_ENGINE_WEB_EVENTS.PLUGIN_UNREGISTER_SKIPPED, {
        reason: "not-found",
        plugin: name || null,
      });
    }

    return removed;
  }

  function create(spec, opts = {}) {
    const mergedOpts = {
      ...opts,
      installWindowExports:
        opts.installWindowExports === undefined
          ? !!installWindowExports
          : !!opts.installWindowExports,
    };

    const eng = createEngine(spec, mergedOpts);
    if (!eng || typeof eng !== "object") {
      emit(TABLE_ENGINE_WEB_EVENTS.INSTANCE_CREATE_FAILED, {
        reason: "create-returned-null",
        spec,
      });
      return eng;
    }

    const ctx = { spec, opts: mergedOpts };

    const entry = {
      eng,
      ctx,
      instanceId: nextInstanceLabel(spec),
      createdAt: Date.now(),
      pluginDisposers: new Map(),
      initializedPlugins: new Set(),
      pluginCleanupDone: false,
    };

    instancesById.set(entry.instanceId, entry);
    wrapDestroyForCleanup(entry);
    engines.push(entry);
    pruneEngines();

    for (const plugin of plugins) {
      runPluginInit(plugin, entry, "create");
    }

    emit(TABLE_ENGINE_WEB_EVENTS.INSTANCE_CREATED, {
      instance: summarizeEntry(entry),
      engine: eng,
      spec,
    });

    return eng;
  }

  function registerSpec(name, spec) {
    const key = String(name || "").trim();
    if (!key) {
      emit(TABLE_ENGINE_WEB_EVENTS.SPEC_REGISTER_SKIPPED, {
        reason: "empty-name",
      });
      return false;
    }
    registry[key] = spec;
    emit(TABLE_ENGINE_WEB_EVENTS.SPEC_REGISTERED, { name: key, spec });
    return true;
  }

  function getSpec(name) {
    const key = String(name || "").trim();
    if (!key) return undefined;
    return registry[key];
  }

  function listSpecNames() {
    return Object.keys(registry);
  }

  function on(eventType, handler, options) {
    if (!eventType || typeof handler !== "function") return () => {};
    const signal =
      options && typeof options === "object" && options.signal ? options.signal : null;

    if (signal?.aborted) return () => {};

    eventBus.addEventListener(String(eventType), handler, options);

    let disposed = false;
    const dispose = () => {
      if (disposed) return;
      disposed = true;
      try {
        off(eventType, handler, options);
      } catch {}
      if (signal && abortHandler) {
        try {
          signal.removeEventListener("abort", abortHandler);
        } catch {}
      }
    };

    const abortHandler = () => dispose();
    if (signal && typeof signal.addEventListener === "function") {
      try {
        signal.addEventListener("abort", abortHandler, { once: true });
      } catch {}
    }

    return dispose;
  }

  function off(eventType, handler, options) {
    if (!eventType || typeof handler !== "function") return;
    eventBus.removeEventListener(String(eventType), handler, options);
  }

  function once(eventType, handler, options = {}) {
    if (!eventType || typeof handler !== "function") return () => {};

    let dispose = () => {};
    const wrapped = (event) => {
      dispose();
      handler(event);
    };
    dispose = on(eventType, wrapped, options);
    return dispose;
  }

  function hasInstance(ref) {
    pruneEngines();
    return !!resolveEntry(ref);
  }

  function findInstances(filter = {}) {
    pruneEngines();
    return resolveEntries(filter).map((entry) => summarizeEntry(entry));
  }

  function findInstance(filter = {}) {
    pruneEngines();
    const entry = resolveEntries(filter)[0] || null;
    if (!isEngineAlive(entry?.eng)) return null;
    return entry.eng;
  }

  function getInstance(ref) {
    pruneEngines();
    const entry = resolveEntry(ref);
    if (!isEngineAlive(entry?.eng)) return null;
    return entry.eng;
  }

  function getInstanceInfo(ref) {
    pruneEngines();
    const entry = resolveEntry(ref);
    if (!isEngineAlive(entry?.eng)) return null;
    return summarizeEntry(entry);
  }

  function listInstances() {
    pruneEngines();
    return engines.filter((entry) => isEngineAlive(entry?.eng)).map((entry) => summarizeEntry(entry));
  }

  function destroyInstance(ref) {
    pruneEngines();
    const entry = resolveEntry(ref);
    if (!entry) return false;
    const eng = entry.eng;
    if (!eng || typeof eng.destroy !== "function") {
      runEntryPluginCleanup(entry, "destroy");
      detachEntry(entry, "destroy");
      removeEngine(eng, "destroy");
      return false;
    }

    try {
      eng.destroy();
      return true;
    } catch (err) {
      reportPluginError("destroy-instance", { name: entry.instanceId }, err);
      emit(TABLE_ENGINE_WEB_EVENTS.INSTANCE_DESTROY_FAILED, {
        reason: "destroy-threw",
        instance: summarizeEntry(entry),
      });
      return false;
    }
  }

  function destroyAllInstances() {
    pruneEngines();
    const ids = engines.map((entry) => entry?.instanceId).filter((id) => !!id);
    let removed = 0;
    for (const id of ids) {
      if (destroyInstance(id)) removed += 1;
    }
    return removed;
  }

  return {
    create,
    registerPlugin,
    unregisterPlugin,
    registerSpec,
    getSpec,
    listSpecNames,
    on,
    off,
    once,
    getInstance,
    getInstanceInfo,
    hasInstance,
    findInstance,
    findInstances,
    listInstances,
    destroyInstance,
    destroyAllInstances,
    apiVersion: TABLE_ENGINE_WEB_API_VERSION,
    events: TABLE_ENGINE_WEB_EVENTS,
    IO: TableEngineIO,
    get plugins() {
      return plugins.slice();
    },
  };
}

export function createTableEngineWebObject(options = {}) {
  const {
    tableEngine = null,
    specRegistry = {},
    installWindowExports = false,
    onPluginInitError = null,
    io = TableEngineIO,
    plugins = null,
  } = options || {};

  const registry = specRegistry && typeof specRegistry === "object" ? specRegistry : {};

  const engine =
    tableEngine && typeof tableEngine === "object"
      ? tableEngine
      : createStandaloneTableEngine({
          specRegistry: registry,
          installWindowExports,
          onPluginInitError,
        });

  const pluginApis =
    plugins && typeof plugins === "object" ? Object.freeze({ ...plugins }) : Object.freeze({});

  const requiredEngineMethods = [
    "create",
    "registerSpec",
    "getSpec",
    "listSpecNames",
    "registerPlugin",
    "unregisterPlugin",
    "getInstance",
    "getInstanceInfo",
    "hasInstance",
    "findInstance",
    "findInstances",
    "listInstances",
    "destroyInstance",
    "destroyAllInstances",
    "on",
    "off",
    "once",
  ];

  for (const name of requiredEngineMethods) {
    if (typeof engine?.[name] !== "function") {
      throw new Error(`TableEngine web object requires engine.${name}().`);
    }
  }

  function getSpec(name) {
    const key = String(name || "").trim();
    if (!key) return undefined;
    return engine.getSpec(key);
  }

  function resolveSpec(specOrName) {
    if (!specOrName) return null;
    if (typeof specOrName === "string") return getSpec(specOrName) || null;
    if (typeof specOrName === "object") return specOrName;
    return null;
  }

  function create(specOrName, opts = {}) {
    const spec = resolveSpec(specOrName);
    if (!spec) {
      const label = typeof specOrName === "string" ? specOrName : String(specOrName ?? "<invalid>");
      throw new Error(`TableEngine.create: unknown table spec '${label}'.`);
    }
    return engine.create(spec, opts);
  }

  function listInstances() {
    return engine.listInstances();
  }

  function findInstances(filter = {}) {
    return engine.findInstances(filter);
  }

  function findInstance(filter = {}) {
    return engine.findInstance(filter);
  }

  function getInstance(ref) {
    return engine.getInstance(ref);
  }

  function getInstanceInfo(ref) {
    return engine.getInstanceInfo(ref);
  }

  function hasInstance(ref) {
    return engine.hasInstance(ref);
  }

  function destroy(ref) {
    return engine.destroyInstance(ref);
  }

  function destroyAll() {
    return engine.destroyAllInstances();
  }

  function ensure(specOrName, opts = {}) {
    const spec = resolveSpec(specOrName);
    if (!spec) {
      const label = typeof specOrName === "string" ? specOrName : String(specOrName ?? "<invalid>");
      throw new Error(`TableEngine.ensure: unknown table spec '${label}'.`);
    }

    const instanceId = normalizedRef(opts?.instanceId || opts?.id);
    if (instanceId) {
      const direct = getInstance(instanceId);
      if (direct) return direct;
    }

    const tableId = normalizedRef(opts?.tableId || spec?.tableId);
    if (tableId) {
      const byTable = findInstance({ tableId });
      if (byTable) return byTable;
    }

    const rootId = normalizedRef(opts?.rootId || spec?.rootId);
    if (rootId) {
      const byRoot = findInstance({ rootId });
      if (byRoot) return byRoot;
    }

    return engine.create(spec, opts);
  }

  function createInstance(specOrName, opts = {}) {
    const instanceEngine = create(specOrName, opts);
    const info = instanceEngine ? getInstanceInfo(instanceEngine) : null;
    return Object.freeze({
      id: info?.id || null,
      info,
      engine: instanceEngine || null,
      destroy: () => {
        if (!instanceEngine) return false;
        if (info?.id) return destroy(info.id);
        return destroy(instanceEngine);
      },
    });
  }

  function on(eventType, handler, options) {
    return engine.on(eventType, handler, options);
  }

  function off(eventType, handler, options) {
    engine.off(eventType, handler, options);
  }

  function once(eventType, handler, options = {}) {
    return engine.once(eventType, handler, options);
  }

  return Object.freeze({
    apiVersion: TABLE_ENGINE_WEB_API_VERSION,
    events: TABLE_ENGINE_WEB_EVENTS,
    create,
    ensure,
    createInstance,
    getSpec,
    hasSpec(name) {
      return !!getSpec(name);
    },
    registerSpec(name, spec) {
      return engine.registerSpec(name, spec);
    },
    listSpecs() {
      return engine.listSpecNames();
    },
    registerPlugin(plugin) {
      return engine.registerPlugin(plugin);
    },
    unregisterPlugin(pluginOrName) {
      return engine.unregisterPlugin(pluginOrName);
    },
    listPlugins() {
      return engine.plugins.slice();
    },
    getInstance,
    getInstanceInfo,
    hasInstance,
    findInstance,
    findInstances,
    listInstances,
    destroy,
    destroyAll,
    on,
    off,
    once,
    get engine() {
      return engine;
    },
    IO: io,
    plugins: pluginApis,
  });
}

export function installTableEngineOnWindow(
  tableEngine,
  {
    globalObject = typeof window !== "undefined" ? window : null,
    io = TableEngineIO,
    extraGlobals = null,
  } = {},
) {
  if (!tableEngine || typeof tableEngine !== "object") return tableEngine;
  if (!globalObject || typeof globalObject !== "object") return tableEngine;

  globalObject.TABLE_SPECS = globalObject.TABLE_SPECS || {};
  globalObject.TableEngine = tableEngine;
  if (io) globalObject.TableEngineIO ||= io;

  if (extraGlobals && typeof extraGlobals === "object") {
    for (const [key, value] of Object.entries(extraGlobals)) {
      if (!key || value === undefined) continue;
      globalObject[key] ||= value;
    }
  }

  return tableEngine;
}

export { TableEngineIO };
